// src/app/pages/admin/AdminTradeRequestsPage.jsx
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, RefreshCcw } from "lucide-react";

import AdminTradeRequestList from "../../features/admin/AdminTradeRequestList.jsx";
import { adminListTradeRequests } from "../../features/admin/adminApi.js";

function Pill({ children }) {
  return (
    <span className="px-2 py-1 rounded-md text-xs bg-gray-800/80 border border-gray-700 text-gray-200">
      {children}
    </span>
  );
}

export default function AdminTradeRequestsPage() {
  // Matches Option A: default export + App.jsx default import
  const [refreshTick, setRefreshTick] = useState(0);

  // Summary card (pending count)
  const pendingSummaryQ = useQuery({
    queryKey: ["adminTradeRequestsSummary", refreshTick],
    queryFn: async () => {
      // Your backend maps UI status PENDING_MODERATION -> DB PENDING
      const data = await adminListTradeRequests({
        status: "PENDING_MODERATION",
        limit: 1,
        offset: 0,
      });
      return { total: Number(data?.total || 0) };
    },
    staleTime: 2_000,
  });

  const pendingTotal = useMemo(
    () => pendingSummaryQ.data?.total ?? 0,
    [pendingSummaryQ.data]
  );

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-800 grid place-items-center text-white">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Trade Requests</h1>
              <p className="text-sm text-gray-400">
                Approve or reject user trade requests. Updates trade status and
                triggers the trade engine lifecycle.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setRefreshTick((x) => x + 1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
          type="button"
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-gray-900 border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white">Pending Moderation</div>
          <Pill>
            {pendingSummaryQ.isLoading ? "Loading…" : `${pendingTotal} pending`}
          </Pill>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Use the table below to filter and action requests.
        </div>

        {pendingSummaryQ.error ? (
          <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
            {pendingSummaryQ.error?.response?.data?.detail ||
              pendingSummaryQ.error?.message ||
              "Failed to load summary."}
          </div>
        ) : null}
      </div>

      {/* Main list */}
      <div className="mt-6 bg-gray-900 border border-gray-800 rounded-2xl p-5">
        {/* refreshKey is optional; only effective if your list uses it to refetch */}
        <AdminTradeRequestList refreshKey={refreshTick} />
      </div>
    </div>
  );
}
